<template>
    <div class="component-container">
        <label :for="inputId" @click="openImage">
            <img v-bind:class="{ inputError: hasError && isDirty }" v-bind:src="imageSrc"/>
            <div class="error-message">{{ isDirty ? errorText : '' }}</div>
            <i v-if="!disabled" class="material-icons delete-icon" @click="deleteImage">delete</i>
        </label>
        <h4 v-if="label">{{ label }}</h4>
        <input ref="file-input" :id="inputId" type="file" accept="image/*" @change="onPicturePicked"/>
    </div>
</template>

<style scoped>
input{
    display: none;
}
img{
    width: 110px;
    height: 150px;
    object-fit: cover;
    border-width: 2px;
    border-style: dashed;
    border-radius: 5px;
    border-color: #afafaf;
    -webkit-transition: all .2s ease 0s;
    -moz-transition: all .2s ease 0s;
    -o-transition: all .2s ease 0s;
    transition: all .2s ease 0s;
}

img.inputError{
    border-color: var(--v-error-base, red);
}

div.error-message{
    word-break: break-word;
    bottom: 8px;
    position: absolute;
    font-size: 18px;
    color: var(--v-error-base, red);
    display: flex;
    text-align: center;
    width: 110px;
    vertical-align: middle;
    align-items: center;
    text-shadow: -1px 0px #ffffff, 1px 0 #ffffff, 0 1px #ffffff, 0 -1px #ffffff;
    cursor: pointer;
}

div.component-container{
    width: 110px;
    height: 150px;
    display: inline-flex;
    margin: 10px;
    position: relative;
    flex-direction: column;
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently
                        supported by Chrome and Opera */
}

i.delete-icon{
    cursor: pointer;
    position: absolute;
    top: 8px;
    right: 8px;
    font-size: 18px;
    -webkit-transition: all .2s ease 0s;
    -moz-transition: all .2s ease 0s;
    -o-transition: all .2s ease 0s;
    transition: all .2s ease 0s;
}

i.delete-icon:hover{
    color: var(--v-error-base, red);
}

.component-container > label {
    height: 150px;
    cursor: pointer;
}

.component-container > h4 {
    bottom: -32px;
    position: absolute;
    align-self: center;
}
</style>


<script>
export default {
    props: {
        disabled: Boolean,
        error: Boolean,
        errorCount: {
            type: [Number, String],
            default: 1
        },
        errorMessages: {
            type: [String, Array],
            default: () => []
        },
        messages: {
            type: [String, Array],
            default: () => []
        },
        readonly: Boolean,
        rules: {
            type: Array,
            default: () => []
            },
        success: Boolean,
        successMessages: {
            type: [String, Array],
            default: () => []
        },
        validateOnBlur: Boolean,
        //
        form: Object,
        imageMIME: String, 
        imageData: String, 
        imageName: String,
        label: {
            type: String,
            default: () => ''
        },
    },
    data() {
        return {
            isDirty: false,
            formAssign: false,
            errorBucket: [],
            hasColor: false,
            hasFocused: false,
            hasInput: false,
            isFocused: false,
            isResetting: false,
            valid: false
        }
    },
    methods: {
        openImage(e){
            if(this.disabled){
                e.preventDefault();
                const image = new Image();
                image.src = this.imageSrc;

                const w = window.open();
                w.document.write(image.outerHTML);
            }else{
                this.isDirty = true;
            }
        },
        deleteImage(e){
            if(e){
                e.preventDefault();
            }
            this.$emit('update:imageMIME', undefined);
            this.$emit('update:imageData', undefined);
            this.$emit('update:imageName', undefined);
            this.$refs['file-input'].value = '';
            this._computedWatchers.imageSrc.evaluate();
            this.$forceUpdate();
        },
        transformBase64Data(e) {
            var self = this;
            const files = e.target.files;
            if (files[0] !== undefined) {
                const fr = new FileReader();
                fr.readAsDataURL(files[0])
                fr.addEventListener('load', () => {
                    const data = fr.result.replace(/^data:([A-Za-z-+/]+);base64,/, '');
                    const mime = fr.result.replace(/^data:|(;base64,.+)/g, '');
                    const name = files[0].name;
                    self.$emit('update:imageMIME', mime);
                    self.$emit('update:imageData', data);
                    self.$emit('update:imageName', name);
                    self.isDirty = true;
                    self._computedWatchers.imageSrc.evaluate();
                    self.$forceUpdate();
                });
            }
        },
        onPicturePicked(e) {
            this.transformBase64Data(e);
        },

        // //VForm validatable
        /** @public */
        reset () {
            this.isResetting = true
            this.deleteImage();
        },
        /** @public */
        resetValidation () {
            this.isResetting = true
        },
        /** @public */
        validate (force = false, value = this.internalValue) {
            const errorBucket = []

            if (force) this.hasInput = this.hasFocused = true

            for (let index = 0; index < this.rules.length; index++) {
                const rule = this.rules[index]
                const valid = typeof rule === 'function' ? rule(value) : rule

                if (valid === false || typeof valid === 'string') {
                    errorBucket.push(valid)
                } else if (valid !== true) {
                    console.log(`Rules should return a string or boolean, received '${typeof valid}' instead`, this)
                }
            }

            this.errorBucket = errorBucket
            this.valid = errorBucket.length === 0
            return this.valid
        }
    },
    mounted() {
    },
    beforeMount () {
        this.validate()
    },
    created () {
        this.form && this.form.register(this);
    },

    beforeDestroy () {
        this.form && this.form.unregister(this);
    },
    computed: {
        inputId(){
            return 'image-upload' + this._uid
        },
        imageSrc() {
            if(this.imageMIME && this.imageData){
                return `data:${this.imageMIME};base64,${this.imageData}`;
            }else{
                return require('../assets/imagePickerDefault.png');
            }
        },
        errorText() {
            return this.errorBucket[0];
        },
        internalValue () {
            var value = {
                valid: !!this.imageMIME && !!this.imageData && !!this.imageName,
                size: 4 * this.imageSrc.length / 3,
                type: this.imageMIME
            }
            return value;
        },
        hasError () {
            return this.internalErrorMessages.length > 0 ||
                this.errorBucket.length > 0 ||
                this.error
        },
        externalError () {
            return this.internalErrorMessages.length > 0 || this.error
        },
        // TODO: Add logic that allows the user to enable based
        // upon a good validation
        hasSuccess () {
            return this.successMessages.length > 0 ||
                this.success
        },
        hasMessages () {
            return this.validations.length > 0
        },
        hasState () {
            return this.hasSuccess || (this.shouldValidate && this.hasError)
        },
        internalErrorMessages () {
            return this.errorMessages || ''
        },
        shouldValidate () {
            return this.externalError || (!this.isResetting && (
                this.validateOnBlur
                ? this.hasFocused && !this.isFocused
                : (this.hasInput || this.hasFocused)
            ))
        },
        validations () {
            return this.validationTarget.slice(0, this.errorCount)
        },
        validationState () {
            if (this.hasError && this.shouldValidate) return 'error'
            if (this.hasSuccess) return 'success'
            if (this.hasColor) return this.color
            return null
        },
        validationTarget () {
            const target = this.internalErrorMessages.length > 0
                ? this.errorMessages
                : this.successMessages.length > 0
                ? this.successMessages
                : this.messages

            // String
            if (!Array.isArray(target)) {
                return [target]
            // Array with items
            } else if (target.length > 0) {
                return target
            // Currently has validation
            } else if (this.shouldValidate) {
                return this.errorBucket
            } else {
                return []
            }
        }
    },
    watch: {
        imageData() {
            this.isDirty= true;
        },
        form: {
            handler () {
                if(!this.formAssign && !!this.form){
                    this.formAssign = true;
                    this.form.register(this);
                }
            },
        },
        rules: {
            handler (newVal, oldVal) {
                if (deepEqual(newVal, oldVal)) return
                this.validate()
            },
            deep: true
        },
        internalValue () {
            // If it's the first time we're setting input,
            // mark it with hasInput
            this.hasInput = true
            this.validateOnBlur || this.$nextTick(this.validate)
        },
        isFocused (val) {
            // Should not check validation
            // if disabled or readonly
            if (
                !val &&
                !this.disabled &&
                !this.readonly
            ) {
                this.hasFocused = true
                this.validateOnBlur && this.validate()
            }
        },
        isResetting () {
            setTimeout(() => {
                this.hasInput = false
                this.hasFocused = false
                this.isResetting = false
            }, 0)
        },
        hasError (val) {
            if (this.shouldValidate) {
                this.$emit('update:error', val)
            }
        },
        hasFocused (val){
            if(val)this.isDirty = true;
        },
    }
}

export function deepEqual (a, b) {
  if (a === b) return true

  if (a instanceof Date && b instanceof Date) {
    // If the values are Date, they were convert to timestamp with getTime and compare it
    if (a.getTime() !== b.getTime()) return false
  }

  if (a !== Object(a) || b !== Object(b)) {
    // If the values aren't objects, they were already checked for equality
    return false
  }

  const props = Object.keys(a)

  if (props.length !== Object.keys(b).length) {
    // Different number of props, don't bother to check
    return false
  }

  return props.every(p => deepEqual(a[p], b[p]))
}
</script>